import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { EnvironmentService } from '@sg/common';
import { SgDialogService } from '@sg/ui-kit/core/src/lib/components/sg-dialog/sg-dialog.service';
import { SgMenuGroup, SgMenuItemEvent } from '@sg/ui-kit/core/src/lib/components/sg-menu/sg-menu.model';
import {
  DemoBase,
  DemoLenderType,
  DemoName,
  deleteAdhocSkin,
  effectiveLenderType,
  getActiveDemo,
  getActiveSkin,
  getCode,
  isAdhocSkin,
  restorePreDemoApiEnv,
  savePreDemoApiEnv,
  setActiveDemo,
  setSkinOverrides,
} from '../../environments/demo-flag';

// Matches a git short hash (hex, 6+ chars). Filters out CI placeholders like
// `xxxxxx` that show up when the `meta__hash` env var isn't wired through.
const SHORT_HASH = /^[a-f0-9]{6,}$/i;

// API targets the local dev environment can point at — see environment.ts
// for the `https://api-${API_ENV}.sharegain.com` swap and the MSW gate.
const API_ENV_OPTIONS = [
  'demo',
  'mock',
  'borrowers',
  'dev',
  'mercury',
  'moon',
  'perf',
  'pluto',
  'poolaris',
  'sales',
  'test',
  'venus',
] as const;

// Hash-driven role flags consumed by *.routes.ts at module load. Each entry's
// key matches both the localStorage key and the `#key=true|false` hash trick
// the routes files already implement.
const ROLE_FLAGS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'isRnd', label: 'R&D' },
  { key: 'isSFR', label: 'SFR' },
  { key: 'isLRR', label: 'LRR' },
  { key: 'isBFV', label: 'BFV' },
  { key: 'isAgentManagement', label: 'Agent' },
];

function blankEditingState(): SkinEditorState {
  return { base: 'american', companyName: '', lenderType: 'super-lender', firstName: '', lastName: '' };
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Local state for the skin-editor dialog. Bound two-way against the inputs in
 * the projected ng-template; flushed to localStorage via setSkinOverrides on
 * Save. Fields that get auto-derived (entityLabel, email) are NOT here — they
 * fall out of companyName + lenderType + code at read time.
 */
interface SkinEditorState {
  base: DemoBase;
  companyName: string;
  lenderType: DemoLenderType;
  firstName: string;
  lastName: string;
}

@Component({
  selector: 'sg-build-watermark',
  templateUrl: './build-watermark.component.html',
  styleUrls: ['./build-watermark.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SgBuildWatermarkComponent implements OnInit {
  readonly roleFlags = ROLE_FLAGS;
  readonly apiEnvMenuGroups: SgMenuGroup[];

  // All fields are resolved once in the constructor: env.meta is injected at
  // bootstrap and doesn't change during the component's lifetime, so binding
  // these as getters would re-run their checks on every change-detection cycle.
  //
  // displayEnv: in deployed builds (meta.id is a real build ID) the static
  // env.env is the source of truth. In local dev env.env is hardcoded to 'test'
  // but the API target varies via the `#apiEnv=…` hash + localStorage trick —
  // so fall back to that in local dev only (meta.id === 'local').
  readonly displayEnv: string;
  // Only local dev reads `apiEnv` from localStorage to pick an API host, so
  // showing the selector elsewhere would be misleading — it would change the
  // value but not the target.
  readonly canEditEnv: boolean;
  readonly hash: string | undefined;
  readonly id: string | undefined;
  readonly version: string | undefined;
  readonly visible: boolean;
  /**
   * Demo env shows a minimal watermark (e.g. `demo (american)`) instead of
   * the full dev banner — keeps a customer-facing demo professional while
   * still leaving a "this is a sandbox" hint.
   */
  readonly demoMinimalMode: boolean;
  readonly demoLabel: string;
  readonly demoCodeLabel: string;
  readonly demoBaseLabel: string;
  readonly demoLenderLabel: string;
  readonly activeDemo: DemoName | null;

  /**
   * Options for the demo-settings "Base data" dropdown.
   *
   * Typed as `Record<DemoBase, string>` ON PURPOSE: the template used to hardcode
   * one `<option>` per base, so adding a base to `DemoBase` left the dropdown
   * silently stale (the `swiss` base shipped invisible for exactly this reason).
   * With an exhaustive Record, a new base that has no label here is a COMPILE
   * error instead of a missing menu entry.
   */
  readonly baseDataOptions: ReadonlyArray<{ value: DemoBase; label: string }> = (
    Object.entries({
      american: 'American (US tickers, USD)',
      greek: 'Greek (ATHEX tickers, EUR)',
      turkish: 'Turkish (BIST tickers, USD)',
      swiss: 'Swiss (SIX tickers, USD)',
      uk: 'UK (LSE tickers, GBP)',
      lux: 'Lux (Benelux tickers, Euro)',
      rbc: 'RBC (global tickers, USD, incl. bonds)',
      // `Object.entries` widens keys to `string`; the `satisfies` above is what keeps
      // this exhaustive, so a new DemoBase is still a compile error.
    } satisfies Record<DemoBase, string>) as Array<[DemoBase, string]>
  ).map(([value, label]) => ({ value, label }));

  /** Two-way bound state for the skin-editor dialog (slotContent template). */
  editingSkin: SkinEditorState = blankEditingState();
  /** Active skin's base + name pinned at dialog-open time so the description
   *  text and reset target survive the user typing into the inputs. */
  editingSkinName = '';
  editingSkinBaseLabel = '';
  /** True when the editing skin lives only in localStorage (no DEMO_SKINS
   *  entry). Drives the bottom-link affordance: code-registered skins get
   *  "Reset to code default" (drops overrides); ad-hoc skins get "Delete
   *  this demo" (wipes the whole skin entry + exits demo mode). */
  editingSkinIsAdhoc = false;

  @ViewChild('skinEditorTemplate', { static: true })
  skinEditorTemplate!: TemplateRef<unknown>;

  constructor(
    public env: EnvironmentService,
    private dialogService: SgDialogService,
    private cdr: ChangeDetectorRef
  ) {
    const meta = env.meta;
    // The apiEnv localStorage swap only takes effect in environment.ts (the
    // local dev build) — deployed env files hardcode their API base URLs and
    // ignore localStorage. Gate the menu on hostname so we don't offer a
    // selector that silently does nothing on real test/sales hosts.
    const hostname = typeof location !== 'undefined' ? location.hostname : '';
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
    this.canEditEnv = isLocalHost;
    this.displayEnv = isLocalHost
      ? (typeof localStorage !== 'undefined' && localStorage.getItem('apiEnv')) || 'dev'
      : env.env;
    this.hash = meta?.hash && SHORT_HASH.test(meta.hash) ? meta.hash : undefined;
    this.id = meta?.id && meta.id !== 'local' && meta.id !== meta.version ? meta.id : undefined;
    this.version = meta?.version && meta.version !== 'local' ? meta.version : undefined;

    // The dedicated `demo` env gets a minimal "demo" pill so a prospect
    // share-screen doesn't get a developer-noise banner. `sales` keeps its
    // original (hidden) behaviour for the real-backend sales build.
    //
    // Crucially: `env.env === 'demo'` is also true for plain `apiEnv=mock`
    // dev sessions (environment.ts forces env to 'demo' whenever MSW is on).
    // The minimal pill is meant only for actual prospect demos — when there's
    // no active prospect, fall through to the full developer banner instead.
    const activeDemo = getActiveDemo();
    this.activeDemo = activeDemo;
    const isDemoEnv = (env.env as string) === 'demo';
    this.demoMinimalMode = isDemoEnv && !!activeDemo;
    // Watermark pill reads the skin's `code` (editable via the demo-settings
    // dialog) so reps can re-brand on stage without touching the URL slug. Falls
    // back to the slug if no code is set. Includes base data + lender type so
    // the rep can see at a glance what dataset + lender mode they're on without
    // opening the dialog.
    const skinForLabel = getActiveSkin();
    const skinCode = skinForLabel ? getCode(skinForLabel) : null;
    const baseLabel = skinForLabel ? capitalize(skinForLabel.base) : '';
    const lenderLabel = skinForLabel
      ? effectiveLenderType(skinForLabel) === 'super-lender'
        ? 'Super-lender'
        : 'Standard lender'
      : '';
    this.demoCodeLabel = this.demoMinimalMode ? skinCode || activeDemo || '' : '';
    this.demoBaseLabel = this.demoMinimalMode ? baseLabel : '';
    this.demoLenderLabel = this.demoMinimalMode ? lenderLabel : '';
    // Kept for backwards-compat / aria-label / tooltips; the pill itself
    // renders each field in its own span so they can be color-coded.
    this.demoLabel = this.demoMinimalMode
      ? ['demo', this.demoCodeLabel, this.demoBaseLabel, this.demoLenderLabel].filter(Boolean).join(' · ')
      : '';

    // Prod and sales stay hidden; demo gets the minimal pill; everything else
    // (dev/test/CI) keeps the full developer banner.
    this.visible = env.env !== 'prod' && (env.env as string) !== 'sales';
    this.apiEnvMenuGroups = [
      {
        id: 'apiEnv',
        label: 'API environment',
        singleSelect: true,
        items: API_ENV_OPTIONS.map((envName) => ({
          id: envName,
          label: envName,
          isSelected: envName === this.displayEnv,
        })),
      },
    ];
  }

  isFlagOn(key: string): boolean {
    return typeof localStorage !== 'undefined' && !!localStorage.getItem(key);
  }

  onEnvMenuSelect(event: SgMenuItemEvent): void {
    const value = event.item.id;
    if (!value || value === this.displayEnv) return;
    // Switching INTO a pseudo-env (mock/demo) — remember the current real env
    // so exit-demo can put the user back where they were. Helper no-ops if
    // the current value is already a pseudo-env or if a save already exists.
    if (value === 'mock' || value === 'demo') {
      savePreDemoApiEnv(localStorage.getItem('apiEnv'));
    }
    localStorage.setItem('apiEnv', value);
    // Switching to the `demo` pseudo-env should land in a working prospect demo,
    // not the empty no-demo state. Pre-select Greek as the default so the rep
    // doesn't have to click the demo dropdown a second time. The user can still
    // swap to American via the demo dropdown after the reload.
    // `sgDemo` (demo-activation key) lives in sessionStorage — see demo-flag.ts.
    if (value === 'demo' && !sessionStorage.getItem('sgDemo')) {
      setActiveDemo('greek');
    }
    this.reloadWithoutHash();
  }

  /**
   * Leave demo mode entirely — clears the sgDemo flag (sessionStorage),
   * restores the user's pre-demo apiEnv, drops seed markers + mock-config so
   * the next entry runs clean, unregisters the MSW service worker so a lingering
   * registration can't keep intercepting after reload, strips the `?demo=…`
   * query, and reloads. Called from the dialog's "Exit demo" link.
   *
   * Async because SW unregistration is promise-based; we await it (best-effort,
   * never throwing) so the worker is gone before the reload. If the SW APIs are
   * unavailable or unregistration fails, we still reload — exiting must never
   * get stuck.
   */
  async onExitDemo(): Promise<void> {
    // Activation key is session-scoped (sessionStorage) — see demo-flag.ts.
    sessionStorage.removeItem('sgDemo');
    restorePreDemoApiEnv();
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sgDemo:') && key.includes(':seeded')) {
        localStorage.removeItem(key);
      }
    }
    localStorage.removeItem('mock-config');
    const url = new URL(location.href);
    if (url.searchParams.has('demo')) {
      url.searchParams.delete('demo');
      history.replaceState(null, '', url.pathname + url.search);
    }
    await this.unregisterMockServiceWorker();
    location.reload();
  }

  /**
   * Best-effort unregister of MSW's service worker so a lingering registration
   * can't keep intercepting network calls after the user exits the demo.
   * Defensive: guards `navigator.serviceWorker` (absent on insecure origins /
   * older browsers) and swallows any error — exit-demo must never throw or hang.
   * Targets the mockServiceWorker.js registration but, as a safety net, also
   * unregisters any registration whose active script looks like MSW.
   */
  private async unregisterMockServiceWorker(): Promise<void> {
    try {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations
          .filter((reg) => {
            const scriptUrl = reg.active?.scriptURL ?? reg.installing?.scriptURL ?? reg.waiting?.scriptURL ?? '';
            return scriptUrl.includes('mockServiceWorker');
          })
          .map((reg) => reg.unregister().catch(() => undefined))
      );
    } catch {
      // Ignore — a failed unregister must not block exiting the demo.
    }
  }

  ngOnInit(): void {
    if (!this.demoMinimalMode) return;
    // Auto-open the identity editor once per session for an ad-hoc skin that
    // still has an empty user (came in via `?demo=<unknown>&base=...`).
    const skin = getActiveSkin();
    if (!skin || !isAdhocSkin(skin.name)) return;
    if (skin.identity.user.firstName) return;
    const flag = `sgDemoSkinEditorAutoShown:${skin.name}`;
    if (sessionStorage.getItem(flag)) return;
    sessionStorage.setItem(flag, '1');
    // Defer to next tick so the dialog opens after the host app's initial CD.
    setTimeout(() => this.openSkinEditor(), 0);
  }

  /** Ctrl+Shift+I anywhere in the app opens the editor. Mirrors Ctrl+Shift+M
   *  used by the Mock Settings UI. Only active when a demo is running. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.demoMinimalMode) return;
    if (event.ctrlKey && event.shiftKey && (event.key === 'I' || event.key === 'i')) {
      event.preventDefault();
      this.openSkinEditor();
    }
  }

  /** Pencil-button click in the watermark — same entry point as Ctrl+Shift+I. */
  onEditDemoIdentity(): void {
    this.openSkinEditor();
  }

  /**
   * Open the identity editor via SgDialogService. Pre-fills the form from the
   * active skin's current (possibly already-overridden) identity. On Save,
   * writes the diff to localStorage and reloads.
   */
  private openSkinEditor(): void {
    const skin = getActiveSkin();
    if (!skin) return;
    // Pre-fill blank user names with the same defaults the auth profile falls
    // back to ("Saul / Dawes") so the rep sees the actual rendered values and
    // can edit from there, rather than an empty field that silently fills in.
    this.editingSkin = {
      base: skin.base,
      companyName: skin.identity.companyName,
      lenderType: effectiveLenderType(skin),
      firstName: skin.identity.user.firstName.trim() || 'Saul',
      lastName: skin.identity.user.lastName.trim() || 'Dawes',
    };
    this.editingSkinName = skin.name;
    this.editingSkinIsAdhoc = isAdhocSkin(skin.name);
    this.cdr.markForCheck();

    const dialogRef = this.dialogService.open(
      {
        title: 'Demo settings',
        description: 'Pick the base data template and override the customer-facing identity.',
        // icon: 'edit',
        showCloseButton: true,
        okButton: {
          text: 'Save & reload',
          variant: 'primary',
          onClick: () => this.saveSkinOverrides(skin.name, skin.base),
        },
        cancelButton: { text: 'Cancel', variant: 'ghost-secondary' },
        extraButton: {
          text: 'Exit demo',
          variant: 'error',
          onClick: () => this.onExitDemo(),
        },
      },
      { width: '560px' }
    );
    dialogRef.componentInstance.slotContent = this.skinEditorTemplate;
  }

  /** Called from the bottom-link in the slot content. For code-registered
   *  skins this clears the identity-overrides bucket so the skin snaps back to
   *  its DEMO_SKINS source values. For ad-hoc skins (the whole skin lives in
   *  localStorage, not source code) we wipe the skin entry entirely and exit
   *  demo mode — there's no "code default" to revert to. */
  onResetSkinOverrides(): void {
    if (!this.editingSkinName) return;
    if (this.editingSkinIsAdhoc) {
      deleteAdhocSkin(this.editingSkinName);
      this.onExitDemo();
      return;
    }
    setSkinOverrides(this.editingSkinName, null);
    location.reload();
  }

  private saveSkinOverrides(skinName: string, previousBase: DemoBase): void {
    const v = this.editingSkin;
    setSkinOverrides(skinName, {
      base: v.base,
      companyName: v.companyName.trim() || undefined,
      lenderType: v.lenderType,
      user: {
        firstName: v.firstName.trim() || undefined,
        lastName: v.lastName.trim() || undefined,
      },
    });
    // If the base changed, wipe seed markers + scenario-store snapshot so the
    // next page load re-seeds with the new base's preset data (recent searches,
    // CTA cards, etc.). Mirrors what setActiveDemo does on a demo switch.
    if (v.base !== previousBase) {
      localStorage.removeItem('mock-config');
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith(`sgDemo:${skinName}:seeded`)) {
          localStorage.removeItem(key);
        }
      }
    }
    location.reload();
  }

  onFlagToggle(key: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    // Store '' (not null) for the off state so the default-on seeder in
    // dev-flag-defaults.ts doesn't re-apply 'true' on the next page load.
    // Matches the `#isRnd=false` hash-handler convention in the route files.
    localStorage.setItem(key, checked ? 'true' : '');
    this.reloadWithoutHash();
  }

  // Strip the hash before reloading — environment.ts and *.routes.ts re-parse
  // `#apiEnv=…` / `#isRnd=…` at module load and would otherwise overwrite the
  // value the user just picked.
  private reloadWithoutHash(): void {
    if (location.hash) {
      history.replaceState(null, '', location.pathname + location.search);
    }
    location.reload();
  }
}
